import { ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider } from '@kwenta/sdk/types'
import { formatDollars, toWei, truncateNumbers } from '@kwenta/sdk/utils'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'

import GiftIcon from 'assets/svg/app/gift.svg'
import LinkArrowIcon from 'assets/svg/app/link-arrow.svg'
import SNXIcon from 'assets/svg/partners/snx.svg'

import OptimismIcon from 'assets/svg/providers/optimism.svg'
import Button from 'components/Button'
import { BaseCard } from 'components/Card/style'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { EXTERNAL_LINKS } from 'constants/links'
import { PROVIDER_CHAIN_ICONS } from 'constants/network'
import { FEEREBATE_DISABLED } from 'constants/ui'
import useClickOutside from 'hooks/useClickOutside'
import { selectOpPrice } from 'state/earn/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { claimMultipleOpReferralRewards } from 'state/staking/actions'
import { selectOpReferralRewards, selectSnxRewards } from 'state/staking/selectors'
import media from 'styles/media'

const BalanceActions: FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const theme = useTheme()
	const opPrice = useAppSelector(selectOpPrice)
	const snxRewards = useAppSelector(selectSnxRewards)
	const opReferralRewards = useAppSelector(selectOpReferralRewards)
	const [open, setOpen] = useState(false)
	const [rewardBalance, setRewardBalance] = useState(ZERO_WEI)

	const { ref } = useClickOutside(() => setOpen(false))

	const goToFeeRebateBlog = useCallback(() => {
		window.open(EXTERNAL_LINKS.Docs.SnxTradingIncentivesOnBase, '_blank')
		setOpen(false)
	}, [])

	const handleClaimOpReferral = useCallback(() => {
		dispatch(claimMultipleOpReferralRewards())
		setOpen(false)
	}, [dispatch])

	const REWARDS = [
		{
			key: 'snx-rewards',
			title: t('dashboard.rewards.snx-rewards.title'),
			label: t('dashboard.rewards.est-reward'),
			button: t('dashboard.rewards.more-info'),
			network: t('dashboard.rewards.snx-rewards.network'),
			provider: PerpsProvider.SNX_V3_BASE,
			linkIcon: true,
			onClick: goToFeeRebateBlog,
			hidden: FEEREBATE_DISABLED,
			rewards: [
				{
					rewardIcon: <SNXIcon width={13} height={10.4} />,
					amount: snxRewards,
				},
			],
		},
		{
			key: 'op-ref-rewards',
			title: t('dashboard.rewards.op-ref-rewards.title'),
			label: t('dashboard.rewards.claimable'),
			button: t('dashboard.rewards.claim'),
			network: t('dashboard.rewards.op-ref-rewards.network'),
			provider: PerpsProvider.SNX_V2_OP,
			disabled: opReferralRewards.eq(ZERO_WEI),
			onClick: handleClaimOpReferral,
			rewards: [
				{
					rewardIcon: <OptimismIcon width={13} height={10.4} />,
					amount: opReferralRewards,
				},
			],
		},
	]

	const reward = useMemo(() => toWei(opPrice).mul(opReferralRewards), [opPrice, opReferralRewards])

	useEffect(() => setRewardBalance(reward), [reward])

	return (
		<div ref={ref}>
			<Button
				size="small"
				mono
				onClick={() => setOpen(!open)}
				style={{
					borderColor: theme.colors.selectedTheme.newTheme.border.color,
				}}
			>
				<StyledGiftIcon style={{ marginRight: '5px' }} width={14} height={12} />
				{formatDollars(rewardBalance, { maxDecimals: 2 })}
			</Button>
			{open && (
				<RewardsTabContainer>
					<CardsContainer>
						{REWARDS.map(
							({
								key,
								title,
								label,
								rewards,
								provider,
								network,
								onClick,
								button,
								linkIcon,
								disabled,
								hidden,
							}) =>
								!hidden && (
									<CardGrid key={key}>
										<FlexDivRowCentered>
											<CategoryLabel size="small" weight="semiBold" capitalized={false}>
												{title}
											</CategoryLabel>
											<FlexDivRowCentered columnGap="5px">
												<Body size="small" color="secondary">
													{`${network}`}
												</Body>
												<NetworkIcon>{PROVIDER_CHAIN_ICONS[provider]}</NetworkIcon>
											</FlexDivRowCentered>
										</FlexDivRowCentered>
										<StyledFlexDivRow>
											<FlexDivCol rowGap="5px">
												<Body size="medium" color="secondary" style={{ width: '100%' }}>
													{label}
												</Body>
												<FlexDivRowCentered columnGap="10px">
													{rewards.map(({ amount, rewardIcon }) => {
														return (
															<FlexDivRowCentered
																columnGap="5px"
																justifyContent="flex-start"
																key={amount.toString()}
															>
																<Body size="medium" mono>
																	{truncateNumbers(amount, 4)}
																</Body>
																{rewardIcon}
															</FlexDivRowCentered>
														)
													})}
												</FlexDivRowCentered>
											</FlexDivCol>
											<StyledButton
												fullWidth
												size="small"
												disabled={disabled}
												onClick={onClick}
												textTransform="uppercase"
											>
												<Body
													size="small"
													color={linkIcon || disabled ? 'secondary' : 'preview'}
													weight="semiBold"
													capitalized={false}
												>
													{button}
													{linkIcon ? (
														<LinkArrowIcon height={8} width={8} style={{ marginLeft: '2px' }} />
													) : null}
												</Body>
											</StyledButton>
										</StyledFlexDivRow>
									</CardGrid>
								)
						)}
					</CardsContainer>
				</RewardsTabContainer>
			)}
		</div>
	)
}

const CategoryLabel = styled(Body)`
	text-transform: uppercase;
`

const NetworkIcon = styled.div`
	display: flex;
	svg {
		height: 11px;
		width: 11px;
	}
`

const StyledButton = styled(Button)`
	margin-left: 50px;
	width: 90px;
	padding: 5px 8px 5px 8px;
	height: 30px;
	border-radius: 100px;
`

const RewardsTabContainer = styled.div`
	z-index: 100;
	position: absolute;
	top: 15px;
	right: 13%;

	${media.lessThan('mdUp')`
		padding: 15px;
	`}

	${media.greaterThan('mdUp')`
		margin-top: 56px;
	`}
`

const CardGrid = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	row-gap: 15px;
`

const CardsContainer = styled(BaseCard)`
	display: flex;
	flex-direction: column;
	width: 100%;
	row-gap: 20px;
	padding: 20px;
	border-radius: 8px;
`

const StyledFlexDivRow = styled(FlexDivRow)`
	column-gap: 50px;
	align-items: center;
`

const StyledGiftIcon = styled(GiftIcon)<{ disabled: boolean }>`
	path {
		fill: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
	}
`

export default BalanceActions
